import { APIContext } from "../../types";
import { makePath } from "../../../../api/url";
import { getJSON, postJSON } from "../../request";
import * as APITypes from "../../../../api/cur8/generated-types/v1/Api";
import { applyPagination } from "../../pagination";
import { applyParams } from "../../../params";
import { ETag } from "../types";

export type BillingApi = ReturnType<typeof createAssignmentAPI>;

export function createAssignmentAPI({
  buildURL,
  fetchJSON,
  fetchPagedJSON,
}: APIContext) {
  function queryAssignments(params: APITypes.AssignmentGetsParams) {
    const path = makePath(["api", "v1", "assignment"]);
    const url = buildURL(path);

    applyParams(url.searchParams, { ...params });
    applyPagination(url.searchParams, { ...params });

    const request = getJSON(url);

    return fetchPagedJSON<APITypes.Assignment>(request);
  }

  function queryAssignmentsFor({
    patientId,
    ...rest
  }: { patientId: string } & Exclude<
    APITypes.AssignmentGetsParams,
    "patientId"
  >) {
    const path = makePath(["api", "v1", "patient", patientId, "assignment"]);
    const url = buildURL(path);
    applyParams(url.searchParams, { ...rest });
    applyPagination(url.searchParams, { ...rest });

    const request = getJSON(url);

    return fetchPagedJSON<APITypes.Assignment>(request);
  }
  function getAssignmentFor(patientId: string, assignmentId: string) {
    const path = makePath([
      "api",
      "v1",
      "patient",
      patientId,
      "assignment",
      assignmentId,
    ]);
    const url = buildURL(path);

    const request = getJSON(url);

    return fetchJSON<APITypes.Assignment>(request);
  }

  function updateAssignmentInfoFor(
    patientId: string,
    assignmentId: string,
    { eTag, ...payload }: APITypes.UpdateAssignmentRequest & ETag
  ) {
    const path = makePath(["api", "v1", "assignment", patientId, assignmentId]);
    const url = buildURL(path);
    const request = postJSON(url, payload);
    request.headers.set("If-Match", eTag);

    return fetchJSON<APITypes.Assignment>(request);
  }

  function createAssignmentFor(
    patientId: string,
    payload: APITypes.CreateAssignmentRequest
  ) {
    const path = makePath(["api", "v1", "assignment", patientId]);
    const url = buildURL(path);
    const request = postJSON(url, payload);

    return fetchJSON<APITypes.Assignment>(request);
  }

  function queryDevicesBy(
    props: APITypes.AssignmentDeviceInventoryQueryItemsParams
  ) {
    const path = makePath(["api", "v1", "assignment", "device-inventory"]);

    const url = buildURL(path);
    applyParams(url.searchParams, { ...props });
    applyPagination(url.searchParams, { ...props });

    const request = getJSON(url);

    return fetchPagedJSON<APITypes.AssignmentDeviceInventory>(request);
  }
  function getDeviceFor(serialNumber: string) {
    const path = makePath([
      "api",
      "v1",
      "assignment",
      "device-inventory",
      serialNumber,
    ]);
    const url = buildURL(path);
    const request = getJSON(url);

    return fetchJSON<APITypes.AssignmentDeviceInventory>(request);
  }

  return {
    queryDevicesBy,
    getDeviceFor,
    queryAssignmentsFor,
    queryAssignments,
    getAssignmentFor,
    createAssignmentFor,
    updateAssignmentInfoFor,
  };
}
